import { isIntercomEvent } from '@mondaygpt/analytics-events'
import {
  Intercom,
  hide,
  onHide,
  onShow,
  onUnreadCountChange,
  show,
  shutdown,
  trackEvent,
  update,
} from '@intercom/messenger-js-sdk'
import type { UserEntity } from 'openapi'

class IntercomService {
  private initialized = false
  private appId: string
  private isOpen = false
  private unreadCount = 0
  private onShowCallbacks: Array<() => void> = []
  private onHideCallbacks: Array<() => void> = []
  private onUnreadCountChangeCallbacks: Array<(count: number) => void> = []

  constructor(appId: string = import.meta.env.VITE_INTERCOM_APP_ID) {
    this.appId = appId
  }

  /**
   * Convert ISO timestamp to Unix timestamp (seconds)
   */
  private convertToUnixTimestamp(isoString: string): number {
    return Math.floor(new Date(isoString).getTime() / 1000)
  }

  /**
   * Custom attributes synced to the Intercom contact. These power outbound
   * campaigns (e.g. "email users who signed up >7 days ago and never finished
   * onboarding"). Keys match Intercom's snake_case convention so they line
   * up cleanly in the campaign builder.
   */
  private buildCustomAttributes(user: UserEntity): Record<string, unknown> {
    const completedVersion = user.onboardingData?.completedVersion ?? 0
    return {
      onboarding_completed: completedVersion > 0,
      onboarding_version: completedVersion,
    }
  }

  /**
   * Check if Intercom has been initialized
   */
  isInitialized(): boolean {
    return this.initialized
  }

  /**
   * Get current open state of the messenger
   */
  getIsOpen(): boolean {
    return this.isOpen
  }

  /**
   * Get current unread count
   */
  getUnreadCount(): number {
    return this.unreadCount
  }

  /**
   * Register a callback for when the messenger is shown
   * @returns Unsubscribe function
   */
  onShow(callback: () => void): () => void {
    this.onShowCallbacks.push(callback)
    return () => {
      const index = this.onShowCallbacks.indexOf(callback)
      if (index > -1) {
        this.onShowCallbacks.splice(index, 1)
      }
    }
  }

  /**
   * Register a callback for when the messenger is hidden
   * @returns Unsubscribe function
   */
  onHide(callback: () => void): () => void {
    this.onHideCallbacks.push(callback)
    return () => {
      const index = this.onHideCallbacks.indexOf(callback)
      if (index > -1) {
        this.onHideCallbacks.splice(index, 1)
      }
    }
  }

  /**
   * Register a callback for when the unread count changes
   * @returns Unsubscribe function
   */
  onUnreadCountChange(callback: (count: number) => void): () => void {
    this.onUnreadCountChangeCallbacks.push(callback)
    return () => {
      const index = this.onUnreadCountChangeCallbacks.indexOf(callback)
      if (index > -1) {
        this.onUnreadCountChangeCallbacks.splice(index, 1)
      }
    }
  }

  /**
   * Initialize Intercom with user data
   */
  init(user: UserEntity): void {
    if (this.initialized) {
      return
    }

    try {
      Intercom({
        app_id: this.appId,
        user_id: user.id,
        name: user.name,
        email: user.email,
        created_at: this.convertToUnixTimestamp(user.createdAt),
        hide_default_launcher: true,
        alignment: 'left',
        horizontal_padding: 70,
        ...this.buildCustomAttributes(user),
      })

      // Register SDK callbacks to update internal state
      onShow(() => {
        this.isOpen = true
        this.onShowCallbacks.forEach(callback => callback())
      })

      onHide(() => {
        this.isOpen = false
        this.onHideCallbacks.forEach(callback => callback())
      })

      onUnreadCountChange((count: number) => {
        this.unreadCount = count
        this.onUnreadCountChangeCallbacks.forEach(callback => callback(count))
      })

      this.initialized = true
    } catch (error) {
      console.error('Failed to update Intercom user:', error)
    }
  }

  /**
   * Update Intercom with user data
   */
  updateUser(user: UserEntity): void {
    if (!this.initialized) {
      console.warn('Intercom not initialized. Call init() first.')
      return
    }

    try {
      update({
        user_id: user.id,
        name: user.name,
        email: user.email,
        created_at: this.convertToUnixTimestamp(user.createdAt),
        ...this.buildCustomAttributes(user),
      })
    } catch (error) {
      console.error('Failed to update Intercom user:', error)
    }
  }

  /**
   * Clear Intercom user data (useful for logout)
   */
  logout(): void {
    if (!this.initialized) {
      console.warn('Intercom not initialized. Call init() first.')
      return
    }

    try {
      shutdown()
      this.initialized = false
      this.isOpen = false
      this.unreadCount = 0
    } catch (error) {
      console.error('Failed to logout from Intercom:', error)
    }
  }

  /**
   * Track an event in Intercom.
   *
   * Only events flagged `intercom: true` in the analytics-events YAML are sent.
   * Intercom caps a workspace at 120 active event names and answers everything
   * past the cap with `422 Cannot have more than 120 active event names` — with
   * 381 events in the catalogue, the whole analytics fan-out was being rejected.
   *
   * Metadata is sanitized to what Intercom's events endpoint accepts — FLAT
   * primitives only (string/number/boolean), at most 10 keys, strings capped at
   * 255 chars. The analytics bridge forwards rich event properties (nested
   * objects, arrays, nulls) verbatim, and Intercom 422s the whole event for any
   * non-conforming value (F-01: every event was rejected).
   */
  trackEvent(eventName: string, metadata?: Record<string, unknown>): void {
    if (!this.initialized || !isIntercomEvent(eventName)) {
      return
    }

    try {
      trackEvent(eventName, metadata ? sanitizeEventMetadata(metadata) : undefined)
    } catch (error) {
      console.error('Failed to track event in Intercom:', error)
    }
  }

  /**
   * Show the Intercom messenger widget
   */
  show(): void {
    if (!this.initialized) {
      console.warn('Intercom not initialized. Call init() first.')
      return
    }

    try {
      show()
    } catch (error) {
      console.error('Failed to show Intercom messenger:', error)
    }
  }

  /**
   * Hide the Intercom messenger widget
   */
  hide(): void {
    if (!this.initialized) {
      console.warn('Intercom not initialized. Call init() first.')
      return
    }

    try {
      hide()
    } catch (error) {
      console.error('Failed to hide Intercom messenger:', error)
    }
  }
}

/** Flatten analytics metadata to the shape Intercom's events endpoint accepts:
 *  primitives only, ≤10 keys, strings ≤255 chars. Non-primitives are dropped
 *  (arrays/objects would 422 the entire event). Exported for tests. */
export function sanitizeEventMetadata(
  metadata: Record<string, unknown>
): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {}
  for (const [key, value] of Object.entries(metadata)) {
    if (Object.keys(out).length >= 10) break
    if (typeof value === 'string') out[key] = value.slice(0, 255)
    else if (typeof value === 'number' && Number.isFinite(value)) out[key] = value
    else if (typeof value === 'boolean') out[key] = value
  }
  return out
}

// Export singleton instance
export const intercom = new IntercomService()
